import { useCallback, useEffect, useRef, useState } from "react";
import {
  isVoiceRecordingSupported,
  startVoiceRecording,
  type VoiceRecordingSession,
  VOICE_MAX_MS,
} from "../../lib/audio/recordVoiceMessage";

type Props = {
  disabled?: boolean;
  onRecorded: (file: File) => void;
  onError?: (message: string) => void;
};

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export function BoardVoiceButton({ disabled, onRecorded, onError }: Props) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const sessionRef = useRef<VoiceRecordingSession | null>(null);
  const startedAtRef = useRef(0);
  const finishingRef = useRef(false);
  const supported = isVoiceRecordingSupported();

  const stopAndUpload = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || finishingRef.current) return;
    finishingRef.current = true;
    setBusy(true);
    try {
      const file = await session.stop();
      sessionRef.current = null;
      setRecording(false);
      setElapsedMs(0);
      onRecorded(file);
    } catch (e) {
      sessionRef.current = null;
      setRecording(false);
      setElapsedMs(0);
      const msg = e instanceof Error ? e.message : "Не удалось сохранить запись";
      if (msg !== "Запись отменена") onError?.(msg);
    } finally {
      finishingRef.current = false;
      setBusy(false);
    }
  }, [onError, onRecorded]);

  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => {
      const ms = Date.now() - startedAtRef.current;
      setElapsedMs(ms);
      if (ms >= VOICE_MAX_MS) void stopAndUpload();
    }, 250);
    return () => window.clearInterval(id);
  }, [recording, stopAndUpload]);

  useEffect(() => {
    return () => {
      sessionRef.current?.cancel();
      sessionRef.current = null;
    };
  }, []);

  async function start() {
    if (!supported) {
      onError?.("Запись голоса не поддерживается в этом браузере");
      return;
    }
    setBusy(true);
    try {
      const session = await startVoiceRecording({ maxMs: VOICE_MAX_MS });
      sessionRef.current = session;
      startedAtRef.current = Date.now();
      finishingRef.current = false;
      setElapsedMs(0);
      setRecording(true);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.name === "NotAllowedError"
            ? "Нет доступа к микрофону"
            : e.message
          : "Не удалось начать запись";
      onError?.(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onClick() {
    if (disabled || busy) return;
    if (recording) {
      await stopAndUpload();
      return;
    }
    await start();
  }

  const title = !supported
    ? "Запись голоса недоступна"
    : recording
      ? `Идёт запись ${formatElapsed(elapsedMs)} — нажмите, чтобы остановить`
      : "Записать голосовое сообщение";

  return (
    <button
      type="button"
      className={`wb-tool-icon-btn wb-tool-icon-btn--ghost${recording ? " is-recording" : ""}${disabled || !supported ? " is-disabled" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={recording}
      disabled={disabled || !supported || busy}
      onClick={() => void onClick()}
    >
      <svg
        className="wb-tool-icon-btn__svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {recording ? (
          <rect x="7" y="7" width="10" height="10" rx="1.5" fill="currentColor" stroke="none" />
        ) : (
          <>
            <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z" />
            <path d="M19 11a7 7 0 0 1-14 0" />
            <path d="M12 18v3" />
          </>
        )}
      </svg>
    </button>
  );
}
